import React, { useMemo } from "react";
import { X, CheckCircle2, Zap, Star, Crown } from "lucide-react";
import { Modal } from "antd";
import formatPrice from "../../../../utility/formatPrice";
interface AIPlan {
  name: string;
  badge: string;
  priceMonthly?: string;
  priceQuarterly?: string;
  priceYearly?: string;
  features: string[];
  integrationServices: Array<{ name: string; cost: string }>;
  tier: string;
  popular?: boolean;
  isVip?: boolean;
}

interface AIPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: AIPlan;
  /** Called with the selected tier AND the computed integration services total (as a number) */
  onGetStarted: (tier: string, setupFee: number) => void;
  billingType: "Monthly" | "Quarterly" | "Yearly";
  isSelected?: boolean;
  onRemove?: () => void;
  /** Called instead of onGetStarted when plan.isVip is true */
  onContactSales?: () => void;
}

const AIPlanModal: React.FC<AIPlanModalProps> = ({
  isOpen,
  onClose,
  plan,
  onGetStarted,
  billingType,
  isSelected,
  onRemove,
  onContactSales,
}) => {
  const Icon = plan.isVip ? Crown : plan.popular ? Star : Zap;

  /** Sum all parseable cost entries (skip "Custom" / "Contact Sales") */
  const setupFeeTotal = useMemo(() => {
    return plan.integrationServices
      .filter((s) => s.name && s.cost)
      .reduce((sum, s) => {
        const numeric = parseFloat(s.cost.replace(/[₦,\s]/g, ""));
        return sum + (isNaN(numeric) ? 0 : numeric);
      }, 0);
  }, [plan.integrationServices]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="ai-plan-modal"
      closeIcon={<X className="text-gray-400 hover:text-gray-600" />}
    >
      <div className="p-2 font-abel">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-2xl font-medium text-gray-900">{plan.name}</h2>
          </div>
          <p className="inline-block px-3 py-1 text-sm font-medium rounded-full border border-blue-600 text-blue-600 bg-blue-50">
            {plan.badge}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Core Features */}
          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
              Core Features
            </h3>
            <ul className="space-y-3">
              {plan.features.map((feature, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-sm text-gray-700"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Integration Services */}
          <div>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <span className="w-2 h-6 bg-green-600 rounded-full"></span>
                Integration Services
              </h3>
              <span
                className={`text-sm font-medium mb-4 pr-4 ${
                  plan.isVip ? "text-white" : "text-[#212121]"
                }`}
              >
                NGN
              </span>
            </div>
            <div className="max-h-[260px] overflow-y-auto pr-2 space-y-2">
              {plan.integrationServices
                .filter((s) => s.name)
                .map((service, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-start gap-4 p-2 bg-gray-50 rounded text-xs"
                  >
                    <span className="text-gray-700">{service.name}</span>
                    <span className="font-medium text-gray-700 whitespace-nowrap">
                      {service.cost}
                    </span>
                  </div>
                ))}
            </div>
            {/* Integration Services Total */}
            {setupFeeTotal > 0 && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-800">
                  Total Integration Fee
                </span>
                <span className="text-sm font-bold text-green-700">
                  NGN {formatPrice(setupFeeTotal)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500 font-abel">
              {billingType === "Monthly"
                ? "Monthly"
                : billingType === "Quarterly"
                  ? "Quarterly"
                  : "Yearly"}{" "}
              Subscription Price
            </span>
            <div className="flex items-baseline gap-1">
              {plan.isVip ? (
                <span className="text-2xl font-medium text-gray-900">
                  Contact Sales
                </span>
              ) : (
                <>
                  <span className="text-3xl font-medium text-gray-900">
                    {billingType === "Monthly"
                      ? plan.priceMonthly
                      : billingType === "Quarterly"
                        ? plan.priceQuarterly || (parseFloat((plan.priceMonthly || "0").replace(/[₦,\s]/g, "")) * 3).toLocaleString()
                        : plan.priceYearly}
                  </span>
                  <span className="text-gray-500 text-sm">
                    /{billingType === "Monthly" ? "month" : billingType === "Quarterly" ? "quarter" : "year"}
                  </span>
                </>
              )}
            </div>
            {setupFeeTotal > 0 && (
              <span className="text-xs text-gray-500 mt-1">
                + NGN {formatPrice(setupFeeTotal)} integration (one-time)
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isSelected && !plan.isVip ? (
              <button
                onClick={onRemove}
                className="px-8 py-3 bg-white text-red-600 border border-red-600 rounded-lg font-medium hover:bg-red-50 transition shadow-sm"
              >
                Remove Plan
              </button>
            ) : plan.isVip ? (
              <button
                onClick={() => {
                  onClose();
                  onContactSales?.();
                }}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition shadow-lg hover:shadow-blue-200"
              >
                Contact Sales
              </button>
            ) : (
              <button
                onClick={() => onGetStarted(plan.tier, setupFeeTotal)}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition shadow-lg hover:shadow-blue-200"
              >
                Add to Plan
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AIPlanModal;
